import { useId } from 'react';
import styled from 'styled-components';
import {
  apiLoadErrorText,
  useGetEconChronumHoursQuery,
  type ChronumHours,
  type ChronumPayrollCategory,
  type ChronumSnapshotMeta,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconArrowRight, IconRefresh } from '@shared/ui/icons';
import { fleetLeading, fleetType } from '@entities/fleet';
import {
  MACHINE_HOURS,
  MAN_HOURS,
  dataQualityNotes,
  emptyHoursReason,
  formatDateTime,
  formatHours,
  formatInt,
  headcountNote,
  hoursAnomalies,
  hoursTotals,
  isPositiveDecimal,
  marksNote,
  monthState,
  originLabel,
  outsideHours,
  periodLabel,
  periodLabelLower,
  typo,
} from '@entities/chronum';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import { AnomalyList } from './AnomalyList';
import { MachineHoursTable, ManHoursTable } from './HoursTables';
import type { ChronumRefresh } from './useChronumRefresh';
import {
  ArrowLink,
  EmptyBox,
  Eyebrow,
  ListSkeleton,
  NARROW,
  Section,
  SectionHead,
  SectionNote,
  SectionTitle,
} from './ui';

type HoursData = Extract<ChronumHours, { available: true }>;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};

  & ul.companies {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(4)}`};
    margin: ${({ theme }) => `${theme.spacing(1)} 0 0`};
    padding: 0;
    list-style: none;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const SnapshotLine = styled.p`
  margin: 0;
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
  text-wrap: pretty;
`;

/** Итоги месяца крупными цифрами, через волосяные линии. */
const Figures = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin: ${({ theme }) => `0 0 ${theme.spacing(2.5)}`};
  border-top: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  & > div {
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing(1)};
    min-width: 0;
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(4)} 0`};
  }

  & > div + div {
    padding-left: ${({ theme }) => theme.spacing(4)};
    border-left: 1px solid ${({ theme }) => theme.colors.border};
  }

  & dd {
    margin: 0;
    font-size: ${fleetType.title};
    font-weight: 600;
    line-height: ${fleetLeading.tight};
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  & dt {
    font-size: ${fleetType.small};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > div[data-tone='warning'] dd::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: ${({ theme }) => theme.spacing(2)};
    border-radius: 50%;
    vertical-align: 0.2em;
    background: ${({ theme }) => theme.colors.status.warning};
  }

  @container chronum (max-width: ${NARROW}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > div,
    & > div + div {
      padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(3)} 0`};
      border-left: none;
    }

    & > div:nth-child(even) {
      padding-left: ${({ theme }) => theme.spacing(3)};
      border-left: 1px solid ${({ theme }) => theme.colors.border};
    }

    & > div:nth-child(n + 3) {
      border-top: 1px solid ${({ theme }) => theme.colors.border};
    }

    & dd {
      font-size: ${fleetType.lead};
    }
  }
`;

/** Чего стоит цифра численности — сразу под итогами. */
const FiguresNote = styled.p`
  max-width: 920px;
  margin: ${({ theme }) => `0 0 ${theme.spacing(8)}`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;

  & > sup {
    margin-right: 2px;
  }
`;

const Calm = styled.p`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.primary};

  &::before {
    content: '';
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.status.success};
    transform: translateY(-1px);
  }
`;

const Footnote = styled.p`
  margin: ${({ theme }) => `${theme.spacing(3)} 0 0`};
  font-size: ${fleetType.small};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

interface Props {
  period: string;
  configured: boolean;
  statusLoading: boolean;
  /** Последний снимок табеля за месяц (любой итог) — для пустого состояния. */
  lastCredited: ChronumSnapshotMeta | null;
  categories: readonly ChronumPayrollCategory[];
  canWrite: boolean;
  refresh: ChronumRefresh;
  onOpenSite: (siteId: string) => void;
  onOpenUnmapped: () => void;
}

/** «Часы месяца»: свод по объектам и центрам затрат с аномалиями. */
export function HoursPanel(props: Props) {
  const { period } = props;
  const hours = useGetEconChronumHoursQuery(period);
  // currentData — только ответ за выбранный месяц: при смене месяца прошлый свод
  // не показывается под новой подписью, а при перечитывании того же — не мигает.
  const data = hours.currentData;

  if (hours.isError) {
    return (
      <Notice $tone="danger" role="alert">
        <span className="text">
          {apiLoadErrorText(
            hours.error,
            'Не удалось загрузить свод часов.',
            'Нет доступа к своду часов Хронума.',
          )}
        </span>
        <TextButton type="button" onClick={() => void hours.refetch()}>
          Повторить
        </TextButton>
      </Notice>
    );
  }
  if (!data) return <HoursSkeleton />;
  if (!data.available) return <HoursEmpty {...props} />;
  return <HoursView {...props} data={data} refetching={hours.isFetching} />;
}

function HoursSkeleton() {
  return (
    <div aria-busy="true" aria-label="Загрузка свода часов">
      <SkeletonLine $height={44} $radius="10px" />
      <SkeletonLine $height={12} $width="50%" />
      <div>&nbsp;</div>
      <SkeletonLine $height={64} />
      <div>&nbsp;</div>
      <ListSkeleton rows={6} label="Загрузка таблицы часов" />
    </div>
  );
}

function HoursEmpty({ period, configured, statusLoading, lastCredited, canWrite, refresh }: Props) {
  const reason = emptyHoursReason(lastCredited, configured || statusLoading);
  const busy = refresh.running && refresh.runningPeriod === period;
  return (
    <EmptyBox>
      <Eyebrow>{periodLabel(period)}</Eyebrow>
      <h3>{reason.title}</h3>
      <p>{reason.text}</p>
      {canWrite && reason.canRefresh && configured && (
        <div className="actions">
          <Button
            type="button"
            $variant="primary"
            disabled={refresh.starting || busy}
            onClick={() => refresh.run(period)}
          >
            <IconRefresh size={16} />
            {busy ? 'Загружаю…' : 'Обновить'}
          </Button>
        </div>
      )}
    </EmptyBox>
  );
}

function HoursView({
  data,
  refetching,
  period,
  categories,
  canWrite,
  onOpenSite,
  onOpenUnmapped,
}: Props & { data: HoursData; refetching: boolean }) {
  const month = monthState(data);
  // Есть ли в табеле месяца хоть какие-то часы — в свод или мимо него.
  const anyHours =
    data.objects.length > 0 ||
    data.anomalies.unbound_sites.length > 0 ||
    data.anomalies.conflicting_sites.length > 0 ||
    data.anomalies.unattributed.length > 0;
  const marks = anyHours ? marksNote(data.anomalies) : null;
  const snapshot = data.snapshot;

  return (
    <div aria-busy={refetching}>
      <Stack>
        <Notice $tone={month.closed ? 'success' : 'warning'}>
          <span className="text">
            <strong>{month.title}.</strong> {month.text}
            {month.companies.length > 0 && (
              <ul className="companies" aria-label="Месяц по компаниям">
                {month.companies.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            )}
          </span>
        </Notice>
        <SnapshotLine>
          {`Снимок табеля за ${periodLabelLower(period)} — ${formatDateTime(snapshot.fetched_at)}, ${originLabel(snapshot)}.`}
          {data.generated_at ? ` Хронум посчитал часы ${formatDateTime(data.generated_at)}.` : ''}
          {marks ? ` ${marks}` : ''}
        </SnapshotLine>
      </Stack>

      {!anyHours ? (
        <EmptyBox>
          <h3>В табеле месяца часов нет</h3>
          <p>
            {typo(
              `Снимок за ${periodLabelLower(period)} получен, но Хронум не отдал в нём ни одного часа по площадкам. Если работы были, проверьте табель в Хронуме и обновите снимок.`,
            )}
          </p>
        </EmptyBox>
      ) : (
        <HoursBody
          data={data}
          categories={categories}
          canWrite={canWrite}
          onOpenSite={onOpenSite}
          onOpenUnmapped={onOpenUnmapped}
        />
      )}
    </div>
  );
}

function HoursBody({
  data,
  categories,
  canWrite,
  onOpenSite,
  onOpenUnmapped,
}: Pick<Props, 'categories' | 'canWrite' | 'onOpenSite' | 'onOpenUnmapped'> & {
  data: HoursData;
}) {
  const manId = useId();
  const machineId = useId();
  const anomaliesId = useId();
  const totals = hoursTotals(data.objects);
  const outside = outsideHours(data.anomalies);
  const anomalies = hoursAnomalies(data.anomalies);
  const quality = dataQualityNotes(data.data_quality);
  const hasMachines = data.objects.some((o) => o.machine_hours.length > 0);

  return (
    <>
      <Figures aria-label="Итоги месяца">
        <div>
          <dt>{typo(`${MAN_HOURS} по объектам и центрам затрат`)}</dt>
          <dd>{formatHours(totals.manHours)}</dd>
        </div>
        <div>
          <dt>{typo(`${MACHINE_HOURS} групп механизаторов`)}</dt>
          <dd>{formatHours(totals.machineHours)}</dd>
        </div>
        <div>
          <dt>{typo('человек — сумма по площадкам*')}</dt>
          <dd>{formatInt(data.headcount.sum_by_sites)}</dd>
        </div>
        {isPositiveDecimal(outside.total) && (
          <div data-tone="warning">
            <dt>{typo('ч не вошли в свод: вне объектов, с двумя привязками, без площадки')}</dt>
            <dd>{formatHours(outside.total)}</dd>
          </div>
        )}
      </Figures>
      <FiguresNote>
        <sup aria-hidden="true">*</sup>
        {headcountNote(data.headcount)}
      </FiguresNote>

      <Section aria-labelledby={anomaliesId}>
        <SectionHead>
          <SectionTitle id={anomaliesId}>
            {anomalies.length > 0 ? `Требует внимания · ${anomalies.length}` : 'Проверка свода'}
          </SectionTitle>
        </SectionHead>
        {anomalies.length > 0 ? (
          <AnomalyList
            items={anomalies}
            canWrite={canWrite}
            onOpenSite={onOpenSite}
            onOpenUnmapped={onOpenUnmapped}
          />
        ) : (
          <Calm>
            {typo(
              'Всё разнесено: каждая площадка с часами привязана к одному объекту или центру затрат, у всех должностей есть категория ФОТ.',
            )}
          </Calm>
        )}
        {quality.length > 0 && (
          <Footnote>Замечания Хронума к данным месяца: {quality.join(' · ')}.</Footnote>
        )}
      </Section>

      <Section aria-labelledby={manId}>
        <SectionHead>
          <SectionTitle id={manId}>Человеко-часы по объектам</SectionTitle>
          <SectionNote>{typo('по категориям ФОТ, часы табеля после правок')}</SectionNote>
        </SectionHead>
        {data.objects.length > 0 ? (
          <ManHoursTable objects={data.objects} categories={categories} labelledBy={manId} />
        ) : (
          <SectionNote>
            {typo(
              'Ни одна площадка с часами не привязана к объекту или центру затрат — все часы пока вне свода. Привяжите площадки на вкладке «Площадки».',
            )}
          </SectionNote>
        )}
      </Section>

      <Section aria-labelledby={machineId}>
        <SectionHead>
          <SectionTitle id={machineId}>Машино-часы групп механизаторов</SectionTitle>
          <SectionNote>{typo('часы механизаторов × коэффициент группы')}</SectionNote>
        </SectionHead>
        {hasMachines ? (
          <MachineHoursTable objects={data.objects} labelledBy={machineId} />
        ) : (
          <SectionNote>
            {typo(
              'Часы механизаторов не попали ни в одну группу: должности Хронума привязываются к группам механизаторов в справочнике техники.',
            )}{' '}
            <ArrowLink to="/agents/econ/fleet?tab=groups">
              Группы механизаторов
              <IconArrowRight size={14} />
            </ArrowLink>
          </SectionNote>
        )}
      </Section>
    </>
  );
}
